/**
 * Created by piotr.pozniak@thebeaverhead.com on 18/05/2024
 */

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import useDidMountEffect from "../../../../../hooks/useDidMountEffect";
import useWeekPeriod from "../../../../../hooks/useWeekPeriod";
import { isSameDay, startOfDay } from "../../../../../helpers/date";
import sleep from "../../../../../helpers/sleep";
import WeekListItem from "./WeekListItem";

const WeeklistDropdown = ({
  currentWeek,
  showUpcomingEventsOnly,
  onChangeWeek,
  startDate,
  endDate,
}) => {
  const { periodStart, periodEnd, addPeriods, diffPeriods, periodMs } =
    useWeekPeriod();

  const hasDateRange = Boolean(startDate && endDate);

  const [showWeeksDropdown, setShowWeeksDropdown] = useState(false);

  const [state, setState] = useState(() => ({
    scrollTop: false,
    scrollBottom: false,
    scrollTopPreflight: false,
    weekListStart: hasDateRange
      ? periodStart(startDate)
      : showUpcomingEventsOnly
      ? periodStart(currentWeek)
      : addPeriods(currentWeek, -2),
  }));

  const totalWeeks = useMemo(() => {
    if (!hasDateRange) return 0;
    return Math.ceil((endDate - startDate) / periodMs);
  }, [hasDateRange, startDate, endDate, periodMs]);

  const canScrollWeeksTop =
    (!showUpcomingEventsOnly ||
      diffPeriods(state.weekListStart, new Date()) > 4) &&
    (!hasDateRange ||
      (totalWeeks > 5 &&
        !isSameDay(state.weekListStart, periodStart(startDate))));

  const canScrollWeeksBottom =
    !hasDateRange || Math.abs(diffPeriods(state.weekListStart, endDate)) > 5;

  useDidMountEffect(() => {
    if (state.scrollTop || state.scrollBottom) {
      setState((prev) => ({ ...prev, scrollTopPreflight: true }));

      sleep(0)
        .then(() => {
          setState((prev) => ({ ...prev, scrollTopPreflight: false }));
          return sleep(600);
        })
        .then(() => {
          setState((prev) => ({
            ...prev,
            scrollTop: false,
            scrollBottom: false,
            weekListStart: addPeriods(
              prev.weekListStart,
              prev.scrollTop ? -5 : 5
            ),
          }));
        });
    }
  }, [state.scrollBottom, state.scrollTop]);

  /**
   *
   * @param week {Date} start of the clicked period
   * @returns {function(*): void}
   */
  const onClickWeekList = (week) => (e) => {
    // When showing only upcoming events, the list starts at the current
    // period, so we can't show more periods before the clicked one than
    // there are between it and the current period.
    const periodsBefore = showUpcomingEventsOnly
      ? Math.max(0, Math.min(diffPeriods(week, new Date()), 2))
      : 2;

    setShowWeeksDropdown(false);
    setState((prev) => ({
      ...prev,
      weekListStart: addPeriods(week, -periodsBefore),
    }));

    onChangeWeek(week);
  };

  const onMoverClick = (direction) => (e) => {
    setState((prev) => {
      const data = { ...prev, [direction]: true };

      if (data.scrollTop) {
        data.scrollTopPreflight = true;
      }
      return data;
    });
  };

  let periods = [];

  if (hasDateRange) {
    let cursor = state.scrollTop
      ? addPeriods(state.weekListStart, -5)
      : periodStart(state.weekListStart);

    const rangeStart = startOfDay(startDate);

    while (startOfDay(cursor) <= endDate) {
      if (periodEnd(cursor) >= rangeStart) {
        periods.push(cursor);
      }
      cursor = addPeriods(cursor, 1);
    }
  } else {
    const count = state.scrollBottom || state.scrollTop ? 10 : 5;
    const offset = state.scrollTop ? -5 : 0;

    periods = [...Array(count).keys()].map((idx) =>
      addPeriods(state.weekListStart, idx + offset)
    );
  }

  const weekListItems = periods.map((weekStart) => (
    <WeekListItem
      key={weekStart.getTime()}
      weekStart={weekStart}
      weekEnd={periodEnd(weekStart)}
      currentWeek={currentWeek}
      onClickWeekList={onClickWeekList}
    />
  ));

  /**
   *
   */
  const onMouseEnterWeekDropdown = () => {
    setShowWeeksDropdown(true);
  };

  /**
   *
   */
  const onMouseLeaveWeekDropdown = () => {
    setShowWeeksDropdown(false);
  };

  return (
    <div
      className={"date-range-selector-week-dropdown-controls"}
      onMouseLeave={onMouseLeaveWeekDropdown}
      onMouseEnter={onMouseEnterWeekDropdown}
    >
      <i className={"material-icons"}>unfold_more</i>
      <div
        className={classnames("date-range-selector-week-dropdown-list", {
          show: showWeeksDropdown,
        })}
      >
        <div
          className={"date-range-selector-week-dropdown-list-mover"}
          onClick={canScrollWeeksTop ? onMoverClick("scrollTop") : null}
        >
          {canScrollWeeksTop ? (
            <i className={"material-icons"}>expand_less</i>
          ) : null}
        </div>
        <div className={"date-range-selector-week-dropdown-list-container"}>
          <ul
            className={classnames({
              scrollTop: !state.scrollTopPreflight && state.scrollTop,
              scrollBottom: state.scrollBottom,
              scrollTopPreflight: state.scrollTopPreflight,
            })}
          >
            {weekListItems}
          </ul>
        </div>
        <div
          className={"date-range-selector-week-dropdown-list-mover"}
          onClick={canScrollWeeksBottom ? onMoverClick("scrollBottom") : null}
        >
          {canScrollWeeksBottom && (!hasDateRange || totalWeeks > 5) ? (
            <i className={"material-icons"}>expand_more</i>
          ) : null}
        </div>
      </div>
    </div>
  );
};

WeeklistDropdown.defaultProps = {};

WeeklistDropdown.propTypes = {
  currentWeek: PropTypes.object.isRequired,
  showUpcomingEventsOnly: PropTypes.bool,
  onChangeWeek: PropTypes.func.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};

export default WeeklistDropdown;
